import cn from 'classnames'
import {
  IKeyInitiativeForm,
  TKeyInitiativeEstimateSalesTargetField,
} from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/models/keyInitiativeForm'
import { TRANSLATION_KEYS as tr } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/translations'
import { formatMoneyInteger } from 'legacy/lut/components/widgets/public/partner360/helpers/formatMoneyInteger'
import { useActualsData } from 'legacy/lut/features/partner360/configuration/hooks/useActualsData'
import { useCurrencyData } from 'legacy/lut/features/partner360/configuration/hooks/useCurrencyData'
import { TReplaceOptions } from 'legacy/lut/library/translation/models/replaceOptions'
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react'

import { NumberInput } from 'cng-common/components/numberInput'
import { FormInput, FormNumberInput, useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { getFormattedFinance } from 'cng-common/helpers/numbers'
import { Nullable } from 'cng-common/models/utils'

import { calculateKeyInitiativeEstimateSales } from './calculateKeyInitiativeEstimateSales'
import { KEY_INITIATIVE_OUTCOME_METRIC_FIELDS } from './config'
import { OutcomeItemsEditor } from './OutcomeItemsEditor'
import { shouldAutoCalculateKeyInitiativeEstimateSales } from './shouldAutoCalculateKeyInitiativeEstimateSales'

import styles from './KeyInitiativeOutcomeSection.module.scss'

const OUTCOME_ICON = 'kbase kbase-trophy'

interface IKeyInitiativeOutcomeSectionProps {
  formKey: string
  initialData?: IKeyInitiativeForm
  isDisabled?: boolean
  isFinancialReadOnly?: boolean
  estimateSalesTargetField: TKeyInitiativeEstimateSalesTargetField
  isIncrementalMode: boolean
  isNoSalesTarget: boolean
  hasEstimateSalesValidationError: boolean
  planValue: number
  originalPlanValue: number
  isGoalWarning: boolean
  isGoalError: boolean
  isGoalBelowMinimum: boolean
}

export const KeyInitiativeOutcomeSection = ({
  formKey,
  initialData,
  isDisabled,
  isFinancialReadOnly = false,
  estimateSalesTargetField,
  isIncrementalMode,
  isNoSalesTarget,
  hasEstimateSalesValidationError,
  planValue,
  originalPlanValue,
  isGoalWarning,
  isGoalError,
  isGoalBelowMinimum,
}: IKeyInitiativeOutcomeSectionProps) => {
  const { currency: defaultCurrency } = useCurrencyData()
  const { actualsValueType: defaultActualsValueType } = useActualsData()
  const [formCurrency] = useGetFormDataField<string | undefined>(formKey, 'currency')
  const [formValueType] = useGetFormDataField<string | undefined>(formKey, 'valueType')
  const [formFunnelSize] = useGetFormDataField<Nullable<number> | undefined>(formKey, 'funnelSize')
  const [formConversionRate] = useGetFormDataField<Nullable<number> | undefined>(
    formKey,
    'conversionRate'
  )
  const [formEstimateSalesTargetValue, setEstimateSalesTargetValue] = useGetFormDataField<
    Nullable<number> | undefined
  >(formKey, estimateSalesTargetField)
  const [isEstimateSalesManuallyEdited, setIsEstimateSalesManuallyEdited] = useGetFormDataField<
    boolean | undefined
  >(formKey, 'isEstimateSalesManuallyEdited')
  const previousEstimateSalesTargetFieldRef = useRef(estimateSalesTargetField)

  const currency = formCurrency || initialData?.currency || defaultCurrency
  const actualsValueType = formValueType || initialData?.valueType || defaultActualsValueType
  const funnelSize =
    formFunnelSize === undefined ? (initialData?.funnelSize ?? null) : formFunnelSize
  const conversionRate =
    formConversionRate === undefined ? (initialData?.conversionRate ?? null) : formConversionRate
  const initialEstimateSalesTargetValue = initialData?.[estimateSalesTargetField] ?? null
  const estimateSalesTargetValue =
    formEstimateSalesTargetValue === undefined
      ? initialEstimateSalesTargetValue
      : formEstimateSalesTargetValue
  const estimateSales = estimateSalesTargetValue
  const initialEstimateSales = initialEstimateSalesTargetValue || null
  const isEstimateSalesAutoCalculated = shouldAutoCalculateKeyInitiativeEstimateSales(
    initialEstimateSales,
    isEstimateSalesManuallyEdited
  )
  const hasPositivePlan = planValue > 0
  const expectedOutcomeTranslation = useMemo(() => {
    const keys = tr.keyInitiativeEditor.investmentFieldGroup
    if (isNoSalesTarget) {
      return keys.expectedOutcomeNoSalesTarget
    }
    if (isIncrementalMode) {
      return hasPositivePlan
        ? keys.expectedOutcomeIncremental
        : keys.expectedOutcomeIncrementalWithoutPlan
    }
    return hasPositivePlan ? keys.expectedOutcome : keys.expectedOutcomeWithoutPlan
  }, [isNoSalesTarget, isIncrementalMode, hasPositivePlan])

  const outcomeLabelReplaceParams: TReplaceOptions[] = useMemo(
    () => [
      {
        key: '{planValue}',
        value: getFormattedFinance(planValue || 0, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
      },
      {
        key: '{leftoverPlanValue}',
        value: getFormattedFinance(planValue || 0, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
      },
      {
        key: '{originalPlanValue}',
        value: getFormattedFinance(originalPlanValue || 0, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
      },
      {
        key: '{coveredPlanValue}',
        value: getFormattedFinance(originalPlanValue - planValue, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
      },
      { key: '{currency}', value: currency || '' },
      { key: '{actualsValueType}', value: actualsValueType || '' },
    ],
    [planValue, originalPlanValue, currency, actualsValueType]
  )

  const goalWarning = useMemo(() => {
    if (!planValue) {
      return tr.keyInitiativeEditor.goalFieldGroup.emptyPlan
    }

    if (isGoalBelowMinimum) {
      return tr.keyInitiativeEditor.goalFieldGroup.lowWarning
    }

    return isGoalError
      ? tr.keyInitiativeEditor.goalFieldGroup.error
      : tr.keyInitiativeEditor.goalFieldGroup.warning
  }, [isGoalBelowMinimum, isGoalError, planValue])

  const goalWarningReplaceParams: TReplaceOptions[] = useMemo(() => {
    const formattedValue = !planValue ? '' : formatMoneyInteger(planValue, 0, 1)

    return [{ key: '{planValue}', value: formattedValue }]
  }, [planValue])

  const setEstimateSales = useCallback(
    (value: Nullable<number>) => {
      setEstimateSalesTargetValue(value)
    },
    [setEstimateSalesTargetValue]
  )

  const handleEstimateSalesChange = useCallback(
    (value: string) => {
      const numericValue = value === '' ? null : Number(value)
      const nextValue = numericValue !== null && Number.isFinite(numericValue) ? numericValue : null

      setIsEstimateSalesManuallyEdited(nextValue !== null)
      setEstimateSales(nextValue)
    },
    [setEstimateSales, setIsEstimateSalesManuallyEdited]
  )

  useEffect(() => {
    if (previousEstimateSalesTargetFieldRef.current === estimateSalesTargetField) {
      return
    }

    previousEstimateSalesTargetFieldRef.current = estimateSalesTargetField
    setIsEstimateSalesManuallyEdited(Boolean(estimateSalesTargetValue))
  }, [estimateSalesTargetField, estimateSalesTargetValue, setIsEstimateSalesManuallyEdited])

  useEffect(() => {
    if (isFinancialReadOnly || isNoSalesTarget || !isEstimateSalesAutoCalculated) {
      return
    }

    const calculatedEstimateSales = calculateKeyInitiativeEstimateSales(funnelSize, conversionRate)
    if (estimateSales !== calculatedEstimateSales) {
      setEstimateSales(calculatedEstimateSales)
    }
  }, [
    conversionRate,
    estimateSales,
    funnelSize,
    isEstimateSalesAutoCalculated,
    isFinancialReadOnly,
    isNoSalesTarget,
    setEstimateSales,
  ])

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <span className={styles.icon}>
          <i className={OUTCOME_ICON} aria-hidden="true" />
        </span>
        <span className={styles.title}>
          <Translation
            text={expectedOutcomeTranslation}
            replaceParams={outcomeLabelReplaceParams}
            span
          />
        </span>
      </header>

      <div className={styles.fields}>
        {KEY_INITIATIVE_OUTCOME_METRIC_FIELDS.map((config) => {
          const isFinancialMetric =
            config.field === 'funnelSize' ||
            config.field === 'conversionRate' ||
            config.field === 'estimateSales'
          const isHiddenSalesMetric = isNoSalesTarget && isFinancialMetric

          if (config.field === 'estimateSales') {
            return (
              <Fragment key={config.field}>
                <div
                  className={cn(styles.field, { [styles.hiddenSalesMetric]: isHiddenSalesMetric })}
                  aria-hidden={isHiddenSalesMetric}
                >
                  <span className={cn(styles.label, styles.estimateSalesLabel)}>
                    <Translation
                      text={config.labelKey}
                      replaceParams={outcomeLabelReplaceParams}
                      span
                    />
                    {hasEstimateSalesValidationError && <span className={styles.asterisk}>*</span>}
                  </span>
                  <NumberInput
                    value={estimateSales === null ? '' : String(estimateSales)}
                    min={config.min}
                    disabled={isDisabled || isFinancialReadOnly || isHiddenSalesMetric}
                    classes={{
                      inputClasses: {
                        root: cn({
                          [styles.invalidEstimateSales]: hasEstimateSalesValidationError,
                        }),
                      },
                    }}
                    onChange={handleEstimateSalesChange}
                  />
                </div>

                {!isFinancialReadOnly && !isNoSalesTarget && hasPositivePlan && isGoalWarning && (
                  <div className={styles.estimateSalesValidation}>
                    <div
                      className={cn(styles.goalWarning, {
                        [styles.goalJustification]: isGoalError,
                      })}
                    >
                      <Translation text={goalWarning} replaceParams={goalWarningReplaceParams} />
                    </div>

                    {isGoalError && (
                      <FormInput
                        formKey={formKey}
                        field="justification"
                        defaultValue={initialData?.justification || ''}
                        type="text"
                        label={tr.keyInitiativeEditor.goalFieldGroup.justification}
                      />
                    )}
                  </div>
                )}
              </Fragment>
            )
          }

          return (
            <div
              key={config.field}
              className={cn(styles.field, { [styles.hiddenSalesMetric]: isHiddenSalesMetric })}
              aria-hidden={isHiddenSalesMetric}
            >
              <span className={styles.label}>
                <Translation
                  text={config.labelKey}
                  replaceParams={
                    config.field === 'funnelSize' ? outcomeLabelReplaceParams : undefined
                  }
                  span
                />
              </span>
              <FormNumberInput
                formKey={formKey}
                field={config.field}
                defaultValue={initialData?.[config.field] ?? null}
                min={config.min}
                max={config.max}
                isDisabled={
                  isDisabled || (isFinancialReadOnly && isFinancialMetric) || isHiddenSalesMetric
                }
              />
            </div>
          )
        })}

        <div className={cn(styles.field, styles.wide)}>
          <OutcomeItemsEditor
            formKey={formKey}
            initialValue={initialData?.outcome}
            isDisabled={isDisabled}
          />
        </div>
      </div>
    </section>
  )
}

KeyInitiativeOutcomeSection.displayName = 'KeyInitiativeOutcomeSection'
